import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '../../components/Button';
import { Field, inputStyle } from '../../components/Field';
import { useAuth } from '../../auth/AuthContext';
import { homePathForRole } from '../../lib/routes';
import { ClientApiError } from '../../api/client';

interface LoginNavigationState {
  justSignedUp?: boolean;
  passwordChanged?: boolean;
}

export function LoginScreen() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homePathForRole(user)} replace />;

  const navState = (location.state ?? null) as LoginNavigationState | null;
  const notice = navState?.passwordChanged
    ? 'Password changed. Please sign in with your new password.'
    : navState?.justSignedUp
      ? 'Account created. A Refer Now dispatcher will approve it before you can send referrals.'
      : '';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const signedIn = await signIn(email, password);
      navigate(homePathForRole(signedIn), { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ClientApiError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <p>
          New here? <Link to="/signup" style={{ color: '#fff' }}>Create an account</Link>
        </p>
      }
    >
      <form onSubmit={submit} noValidate>
        {notice && (
          <div className="notice-banner" role="status">
            {notice}
          </div>
        )}
        {error && (
          <div className="error-banner" role="alert">
            {error}
          </div>
        )}
        <Field label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            style={inputStyle}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            style={inputStyle}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Button type="submit" full disabled={busy}>
          {busy ? 'Signing in' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  );
}
